/**
 * SF-5127 — which companies could not be read again after an action that changed more of them
 * than its answer says: a sending-domain action, done or failed (Remove or a check that finds
 * records missing puts the sender back to ServeFirst, and a check that finds the registration
 * gone saves that, then answers 502), never answers with the sender. What the page holds for
 * such a company may no longer be true, so the parts that depend on it (what customers see) say
 * they could not load it instead.
 *
 * Kept outside React, per company, like companySaveStore: staff may have moved to another
 * company, or off the page, by the time the re-read fails, and the company now on screen must
 * not be marked. Cleared by the next successful read of that company (the page watches its
 * company reads), never by a cache write.
 */
import { useSyncExternalStore } from 'react';

const failed = new Set<string>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((l) => l());

export function markCompanyReadFailed(companyId: string) {
  if (!companyId || failed.has(companyId)) return;
  failed.add(companyId);
  notify();
}

export function clearCompanyReadFailed(companyId: string) {
  if (!failed.delete(companyId)) return;
  notify();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** True while this company's last re-read after such an action failed. Re-renders on change. */
export function useCompanyReadFailed(companyId: string | undefined): boolean {
  return useSyncExternalStore(subscribe, () => (companyId ? failed.has(companyId) : false));
}

/** Tests only: forget every mark between test cases. */
export function resetCompanyReadFailed() {
  failed.clear();
  notify();
}
